
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

interface ChartControlsProps {
  title: string;
  description: string;
  showAverageLine: boolean;
  onShowAverageChange: (checked: boolean) => void;
  useMetricUnit: boolean;
  unitLabel: string;
  handleUnitToggle: () => void;
  valueKey: string;
}

export function ChartControls({
  title,
  description,
  showAverageLine,
  onShowAverageChange,
  useMetricUnit,
  unitLabel,
  handleUnitToggle,
  valueKey
}: ChartControlsProps) {
  const { language } = useLanguage();
  const alternativeUnit = unitLabel === "cm" ? "inches" : unitLabel === "g" ? "lb" : "cm";
  
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h3 className="text-2xl font-semibold leading-none tracking-tight">{title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </motion.div>
      <motion.div 
        className="flex items-center gap-4"
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <div className="flex items-center space-x-2">
          <Switch
            id={`show-average-${valueKey}`}
            checked={showAverageLine}
            onCheckedChange={onShowAverageChange}
            className="transition-all duration-300"
          />
          <Label htmlFor={`show-average-${valueKey}`}>{t('statistics.showAverage', language)}</Label>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleUnitToggle}
          className="transition-all duration-300 hover:bg-accent"
        >
          {useMetricUnit 
            ? `${t('statistics.switchTo', language)} ${alternativeUnit}` 
            : `${t('statistics.switchTo', language)} ${unitLabel === "inches" ? "cm" : "g"}`}
        </Button>
      </motion.div>
    </div>
  );
}
